

import { useState } from "react"
import Button from "../ui/button"
import { Card } from "../ui/card"
import { RefreshCw, Brain } from "lucide-react"

const PracticeGenerator = ({ onSubmit }) => {
  const [curriculum, setCurriculum] = useState("caie")
  const [subject, setSubject] = useState("")
  const [level, setLevel] = useState("")
  const [topic, setTopic] = useState("")
  const [difficulty, setDifficulty] = useState(3)
  const [isLoading, setIsLoading] = useState(false)

  // Comprehensive curriculum data with proper hierarchy
  const curriculumData = {
    caie: {
      name: "Cambridge (CAIE)",
      subjects: {
        mathematics: {
          name: "Mathematics",
          levels: {
            "as-level": {
              name: "AS Level (9709)",
              topics: [
                "Quadratics",
                "Functions",
                "Coordinate Geometry",
                "Circular Measure",
                "Trigonometry",
                "Series",
                "Differentiation",
                "Integration",
                "Vectors",
                "Probability",
                "Kinematics",
                "Forces and Equilibrium",
                "Newton's Laws of Motion",
              ],
            },
            "a-level": {
              name: "A Level (9709)",
              topics: [
                "Algebra and Functions",
                "Complex Numbers",
                "Differential Equations",
                "Numerical Solutions",
                "Vectors in 3D",
                "Differential Calculus",
                "Integral Calculus",
                "Parametric Equations",
                "Polar Coordinates",
                "Hyperbolic Functions",
                "Further Probability",
                "Momentum and Impulse",
                "Work and Energy",
                "Circular Motion",
              ],
            },
            "further-maths": {
              name: "Further Mathematics (9231)",
              topics: [
                "Matrices and Transformations",
                "Series and Induction",
                "Complex Numbers",
                "Hyperbolic Functions",
                "Differential Equations",
                "Polar Coordinates",
                "Vectors in 3D",
                "Eigenvalues and Eigenvectors",
                "Maclaurin and Taylor Series",
                "Elastic Strings and Springs",
                "Motion in a Circle",
                "Gravitation",
                "Statistical Distributions",
                "Hypothesis Testing",
              ],
            },
          },
        },
        physics: {
          name: "Physics",
          levels: {
            "as-level": {
              name: "AS Level (9702)",
              topics: [
                "Physical Quantities and Units",
                "Kinematics",
                "Dynamics",
                "Forces",
                "Work, Energy and Power",
                "Momentum",
                "Matter",
                "Electric Fields",
                "Current of Electricity",
                "D.C. Circuits",
                "Waves",
                "Superposition",
              ],
            },
            "a-level": {
              name: "A Level (9702)",
              topics: [
                "Circular Motion",
                "Gravitational Fields",
                "Temperature",
                "Ideal Gases",
                "Thermodynamics",
                "Oscillations",
                "Electric Fields",
                "Capacitance",
                "Magnetic Fields",
                "Electromagnetic Induction",
                "Alternating Currents",
                "Quantum Physics",
                "Nuclear Physics",
                "Medical Physics",
                "Astronomy and Cosmology",
              ],
            },
          },
        },
        chemistry: {
          name: "Chemistry",
          levels: {
            "as-level": {
              name: "AS Level (9701)",
              topics: [
                "Atomic Structure",
                "Atoms, Molecules and Stoichiometry",
                "Chemical Bonding",
                "States of Matter",
                "Chemical Energetics",
                "Electrochemistry",
                "Equilibria",
                "Reaction Kinetics",
                "Periodicity",
                "Group 2",
                "Group 17",
                "Nitrogen and Sulfur",
                "Introduction to Organic Chemistry",
                "Hydrocarbons",
                "Alcohols",
                "Halogen Derivatives",
                "Carbonyl Compounds",
              ],
            },
            "a-level": {
              name: "A Level (9701)",
              topics: [
                "Lattice Energy",
                "Electrode Potentials",
                "Ionic Equilibria",
                "Reaction Kinetics",
                "Transition Elements",
                "Benzene and Aromatic Compounds",
                "Carboxylic Acids and Derivatives",
                "Nitrogen Compounds",
                "Polymerisation",
                "Organic Synthesis",
                "Analytical Techniques",
                "Chromatography",
                "Spectroscopy",
              ],
            },
          },
        },
        biology: {
          name: "Biology",
          levels: {
            "as-level": {
              name: "AS Level (9700)",
              topics: [
                "Cell Structure",
                "Biological Molecules",
                "Enzymes",
                "Cell Membranes and Transport",
                "Cell and Nuclear Division",
                "Genetic Control",
                "Transport in Plants",
                "Transport in Mammals",
                "Gas Exchange",
                "Infectious Disease",
                "Immunity",
              ],
            },
            "a-level": {
              name: "A Level (9700)",
              topics: [
                "Energy and Respiration",
                "Photosynthesis",
                "Homeostasis",
                "Control and Coordination",
                "Inheritance",
                "Selection and Evolution",
                "Classification, Biodiversity and Conservation",
                "Genetic Technology",
                "Biotechnology",
                "Crop Plants",
                "Aspects of Human Reproduction",
              ],
            },
          },
        },
      },
    },
    ib: {
      name: "International Baccalaureate (IB)",
      subjects: {
        mathematics: {
          name: "Mathematics",
          levels: {
            "aa-sl": {
              name: "Analysis and Approaches SL",
              topics: [
                "Number and Algebra",
                "Functions",
                "Geometry and Trigonometry",
                "Statistics and Probability",
                "Calculus",
              ],
            },
            "aa-hl": {
              name: "Analysis and Approaches HL",
              topics: [
                "Number and Algebra",
                "Functions",
                "Geometry and Trigonometry",
                "Statistics and Probability",
                "Calculus",
                "Proof",
                "Complex Numbers",
                "Vectors",
                "Differential Equations",
              ],
            },
            "ai-sl": {
              name: "Applications and Interpretation SL",
              topics: [
                "Number and Algebra",
                "Functions",
                "Geometry and Trigonometry",
                "Statistics and Probability",
                "Calculus",
                "Financial Mathematics",
              ],
            },
            "ai-hl": {
              name: "Applications and Interpretation HL",
              topics: [
                "Number and Algebra",
                "Functions",
                "Geometry and Trigonometry",
                "Statistics and Probability",
                "Calculus",
                "Financial Mathematics",
                "Graph Theory",
                "Networks",
                "Algorithms",
                "Optimization",
              ],
            },
          },
        },
        physics: {
          name: "Physics",
          levels: {
            sl: {
              name: "Standard Level",
              topics: [
                "Measurements and Uncertainties",
                "Mechanics",
                "Thermal Physics",
                "Waves",
                "Electricity and Magnetism",
                "Circular Motion and Gravitation",
                "Atomic, Nuclear and Particle Physics",
                "Energy Production",
              ],
            },
            hl: {
              name: "Higher Level",
              topics: [
                "Measurements and Uncertainties",
                "Mechanics",
                "Thermal Physics",
                "Waves",
                "Electricity and Magnetism",
                "Circular Motion and Gravitation",
                "Atomic, Nuclear and Particle Physics",
                "Energy Production",
                "Wave Phenomena",
                "Fields",
                "Electromagnetic Induction",
                "Quantum and Nuclear Physics",
              ],
            },
          },
        },
        chemistry: {
          name: "Chemistry",
          levels: {
            sl: {
              name: "Standard Level",
              topics: [
                "Stoichiometric Relationships",
                "Atomic Structure",
                "Periodicity",
                "Chemical Bonding and Structure",
                "Energetics/Thermochemistry",
                "Chemical Kinetics",
                "Equilibrium",
                "Acids and Bases",
                "Redox Processes",
                "Organic Chemistry",
                "Measurement and Data Processing",
              ],
            },
            hl: {
              name: "Higher Level",
              topics: [
                "Stoichiometric Relationships",
                "Atomic Structure",
                "Periodicity",
                "Chemical Bonding and Structure",
                "Energetics/Thermochemistry",
                "Chemical Kinetics",
                "Equilibrium",
                "Acids and Bases",
                "Redox Processes",
                "Organic Chemistry",
                "Measurement and Data Processing",
                "Atomic Structure (AHL)",
                "The Periodic Table (AHL)",
                "Chemical Bonding and Structure (AHL)",
                "Energetics (AHL)",
                "Chemical Kinetics (AHL)",
                "Equilibrium (AHL)",
                "Acids and Bases (AHL)",
                "Redox Processes (AHL)",
                "Organic Chemistry (AHL)",
                "Measurement and Analysis (AHL)",
              ],
            },
          },
        },
        biology: {
          name: "Biology",
          levels: {
            sl: {
              name: "Standard Level",
              topics: [
                "Cell Biology",
                "Molecular Biology",
                "Genetics",
                "Ecology",
                "Evolution and Biodiversity",
                "Human Physiology",
              ],
            },
            hl: {
              name: "Higher Level",
              topics: [
                "Cell Biology",
                "Molecular Biology",
                "Genetics",
                "Ecology",
                "Evolution and Biodiversity",
                "Human Physiology",
                "Nucleic Acids",
                "Metabolism, Cell Respiration and Photosynthesis",
                "Plant Biology",
                "Genetics and Evolution",
                "Animal Physiology",
              ],
            },
          },
        },
      },
    },
    ap: {
      name: "Advanced Placement (AP)",
      subjects: {
        calculus: {
          name: "Calculus",
          levels: {
            ab: {
              name: "Calculus AB",
              topics: [
                "Limits and Continuity",
                "Differentiation: Definition and Fundamental Properties",
                "Differentiation: Composite, Implicit, and Inverse Functions",
                "Contextual Applications of Differentiation",
                "Analytical Applications of Differentiation",
                "Integration and Accumulation of Change",
                "Differential Equations",
                "Applications of Integration",
              ],
            },
            bc: {
              name: "Calculus BC",
              topics: [
                "Limits and Continuity",
                "Differentiation: Definition and Fundamental Properties",
                "Differentiation: Composite, Implicit, and Inverse Functions",
                "Contextual Applications of Differentiation",
                "Analytical Applications of Differentiation",
                "Integration and Accumulation of Change",
                "Differential Equations",
                "Applications of Integration",
                "Parametric Equations, Polar Coordinates, and Vector-Valued Functions",
                "Infinite Sequences and Series",
              ],
            },
          },
        },
        physics: {
          name: "Physics",
          levels: {
            1: {
              name: "Physics 1: Algebra-Based",
              topics: [
                "Kinematics",
                "Dynamics",
                "Circular Motion and Gravitation",
                "Energy",
                "Momentum",
                "Simple Harmonic Motion",
                "Torque and Rotational Motion",
                "Electric Charge and Electric Force",
                "DC Circuits",
                "Mechanical Waves and Sound",
              ],
            },
            2: {
              name: "Physics 2: Algebra-Based",
              topics: [
                "Fluids",
                "Thermodynamics",
                "Electric Force, Field, and Potential",
                "Electric Circuits",
                "Magnetism and Electromagnetic Induction",
                "Geometric and Physical Optics",
                "Quantum Physics, Atomic, and Nuclear Physics",
              ],
            },
            "c-mechanics": {
              name: "Physics C: Mechanics",
              topics: [
                "Kinematics",
                "Newton's Laws of Motion",
                "Work, Energy, and Power",
                "Systems of Particles and Linear Momentum",
                "Circular Motion and Rotation",
                "Oscillations",
                "Gravitation",
              ],
            },
            "c-em": {
              name: "Physics C: Electricity and Magnetism",
              topics: [
                "Electrostatics",
                "Conductors, Capacitors, Dielectrics",
                "Electric Circuits",
                "Magnetic Fields",
                "Electromagnetism",
              ],
            },
          },
        },
        chemistry: {
          name: "Chemistry",
          levels: {
            general: {
              name: "AP Chemistry",
              topics: [
                "Atomic Structure and Properties",
                "Molecular and Ionic Compound Structure and Properties",
                "Intermolecular Forces and Properties",
                "Chemical Reactions",
                "Kinetics",
                "Thermodynamics",
                "Equilibrium",
                "Acids and Bases",
                "Applications of Thermodynamics",
                "Electrochemistry",
              ],
            },
          },
        },
        biology: {
          name: "Biology",
          levels: {
            general: {
              name: "AP Biology",
              topics: [
                "Chemistry of Life",
                "Cell Structure and Function",
                "Cellular Energetics",
                "Cell Communication and Cell Cycle",
                "Heredity",
                "Gene Expression and Regulation",
                "Natural Selection",
                "Ecology",
              ],
            },
          },
        },
        computerScience: {
          name: "Computer Science",
          levels: {
            a: {
              name: "Computer Science A",
              topics: [
                "Primitive Types",
                "Objects",
                "Boolean Expressions and if Statements",
                "Iteration",
                "Writing Classes",
                "Array",
                "ArrayList",
                "2D Array",
                "Inheritance",
                "Recursion",
                "Sorting",
                "Searching",
              ],
            },
            principles: {
              name: "Computer Science Principles",
              topics: [
                "Creative Development",
                "Data",
                "Algorithms and Programming",
                "Computer Systems and Networks",
                "Impact of Computing",
              ],
            },
          },
        },
      },
    },
  }

  // Get the current curriculum data
  const currentCurriculum = curriculumData[curriculum]

  // Get subjects for the current curriculum
  const subjectOptions = Object.keys(currentCurriculum.subjects).map((key) => ({
    value: key,
    label: currentCurriculum.subjects[key].name,
  }))

  // Get levels for the current subject
  const levelOptions =
    subject && currentCurriculum.subjects[subject]
      ? Object.keys(currentCurriculum.subjects[subject].levels).map((key) => ({
          value: key,
          label: currentCurriculum.subjects[subject].levels[key].name,
        }))
      : []

  // Get topics for the current level
  const topicOptions =
    subject && level && currentCurriculum.subjects[subject]?.levels[level]
      ? currentCurriculum.subjects[subject].levels[level].topics
      : []

  const difficultyLabels = ["Beginner", "Elementary", "Intermediate", "Advanced", "Expert"]

  // Handle curriculum change
  const handleCurriculumChange = (e) => {
    const newCurriculum = e.target.value
    setCurriculum(newCurriculum)
    setSubject("")
    setLevel("")
    setTopic("")
  }

  // Handle subject change
  const handleSubjectChange = (e) => {
    setSubject(e.target.value)
    setLevel("")
    setTopic("")
  }

  // Handle level change
  const handleLevelChange = (e) => {
    setLevel(e.target.value)
    setTopic("")
  }

  const generateProblems = async () => {
    if (!subject || !level) {
      alert("Please select a subject and level")
      return
    }

    setIsLoading(true)

    try {
      // Get the display names for the API call
      const curriculumName = currentCurriculum.name
      const subjectName = currentCurriculum.subjects[subject].name
      const levelName = currentCurriculum.subjects[subject].levels[level].name
      const topicName = topic || "General"

      // Make a direct API call to the practice generator endpoint
      const response = await fetch("/api/generate-practice", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          curriculum: curriculumName,
          subject: subjectName,
          level: levelName,
          topic: topicName,
          difficulty: difficultyLabels[difficulty - 1],
        }),
      })

      const data = await response.json()
      console.log("Practice generator API response:", data)

      if (data.success && data.problems && Array.isArray(data.problems) && data.problems.length > 0) {
        // Deep clone the problems to ensure we're not modifying references
        const validProblems = JSON.parse(JSON.stringify(data.problems)).map((problem) => {
          // Ensure all required fields exist and are strings
          return {
            question: typeof problem.question === "string" ? problem.question : "Question not available",
            hint: typeof problem.hint === "string" ? problem.hint : "No hint available",
            answer: typeof problem.answer === "string" ? problem.answer : "Answer not available",
            explanation: typeof problem.explanation === "string" ? problem.explanation : "No explanation available",
          }
        })

        console.log("Validated problems:", validProblems)

        // Pass the configuration and validated problem data to the parent component
        if (onSubmit) {
          const config = {
            curriculum: curriculumName,
            subject: subjectName,
            level: levelName,
            topic: topicName,
            difficulty: difficultyLabels[difficulty - 1],
            problemData: validProblems,
            toolType: "practice",
            type: "practice",
          }
          console.log("Submitting practice problems config:", config)
          onSubmit(config)
        }
      } else {
        console.error("Error generating practice problems:", data.error || "Invalid problem data format")
        fallbackToChatAPI()
      }
    } catch (error) {
      console.error("Error calling practice generator API:", error)
      fallbackToChatAPI()
    } finally {
      setIsLoading(false)
    }
  }

  // Fallback to chat API if direct API fails
  const fallbackToChatAPI = () => {
    if (onSubmit) {
      // Get the display names for the API call
      const curriculumName = currentCurriculum.name
      const subjectName = subject ? currentCurriculum.subjects[subject].name : ""
      const levelName = level && subject ? currentCurriculum.subjects[subject].levels[level].name : ""
      const topicName = topic || "General"

      const config = {
        curriculum: curriculumName,
        subject: subjectName,
        level: levelName,
        topic: topicName,
        difficulty: difficultyLabels[difficulty - 1],
        type: "practice",
        fallback: true,
      }
      onSubmit(config)
    }
  }

  return (
    <div className="w-full">
      <Card className="p-4 bg-white shadow-lg border border-gray-200 rounded-xl">
        <h3 className="text-lg font-semibold mb-4 text-gray-800 flex items-center">
          <Brain className="mr-2 h-5 w-5 text-indigo-600" />
          Practice Problem Generator
        </h3>

        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-1">Curriculum</label>
          <select
            className="w-full p-2 border border-gray-300 rounded-lg text-gray-900 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
            value={curriculum}
            onChange={handleCurriculumChange}
          >
            <option value="caie">Cambridge (CAIE)</option>
            <option value="ib">International Baccalaureate (IB)</option>
            <option value="ap">Advanced Placement (AP)</option>
          </select>
        </div>

        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-1">Subject</label>
          <select
            className="w-full p-2 border border-gray-300 rounded-lg text-gray-900 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
            value={subject}
            onChange={handleSubjectChange}
          >
            <option value="">Select a Subject</option>
            {subjectOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>

        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-1">Level</label>
          <select
            className="w-full p-2 border border-gray-300 rounded-lg text-gray-900 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
            value={level}
            onChange={handleLevelChange}
            disabled={!subject}
          >
            <option value="">Select a Level</option>
            {levelOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>

        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-1">Topic</label>
          <select
            className="w-full p-2 border border-gray-300 rounded-lg text-gray-900 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
            value={topic}
            onChange={(e) => setTopic(e.target.value)}
            disabled={!level}
          >
            <option value="">General (All Topics)</option>
            {topicOptions.map((topic) => (
              <option key={topic} value={topic}>
                {topic}
              </option>
            ))}
          </select>
        </div>

        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Difficulty: {difficultyLabels[difficulty - 1]}
          </label>
          <input
            type="range"
            min="1"
            max="5"
            step="1"
            value={difficulty}
            onChange={(e) => setDifficulty(Number.parseInt(e.target.value))}
            className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer text-gray-900"
          />
          <div className="flex justify-between text-xs text-gray-500 mt-1">
            {difficultyLabels.map((label, i) => (
              <span key={label} className={i === difficulty - 1 ? "font-bold text-indigo-600" : ""}>
                {i + 1}
              </span>
            ))}
          </div>
        </div>

        <Button
          onClick={generateProblems}
          className="w-full h-16 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg"
          disabled={isLoading || !subject || !level}
        >
          {isLoading ? (
            <span className="flex items-center">
              <RefreshCw className="animate-spin h-4 w-4 mr-2" />
              Generating...
            </span>
          ) : (
            "Generate Practice Problems"
          )}
        </Button>
      </Card>
    </div>
  )
}

export default PracticeGenerator
